import {
  Close as CloseIcon,
  Reply as ReplyIcon,
  CheckCircle as ResolveIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { HelpTicket, TicketResponse } from '../../../services/helpService';
import { BaseModal } from '../../common/UI/BaseModal';
import { formatTimestamp, getPriorityStyle, getStatusStyle } from './utils';

interface HelpdeskTicketDialogProps {
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
  selectedTicket: HelpTicket | null;
  canUpdateTickets: boolean;
  statusForm: string;
  handleStatusFormChange: (event: SelectChangeEvent<string>) => void;
  statusReason: string;
  handleStatusReasonChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleUpdateStatus: () => void;
  handleQuickResolve: () => void;
  handleQuickClose: () => void;
  responseForm: string;
  handleResponseFormChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddResponse: () => void;
}

export const HelpdeskTicketDialog: React.FC<HelpdeskTicketDialogProps> = ({
  openDialog,
  setOpenDialog,
  selectedTicket,
  canUpdateTickets,
  statusForm,
  handleStatusFormChange,
  statusReason,
  handleStatusReasonChange,
  handleUpdateStatus,
  handleQuickResolve,
  handleQuickClose,
  responseForm,
  handleResponseFormChange,
  handleAddResponse,
}) => {
  const theme = useTheme();

  return (
    <BaseModal
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      maxWidth="md"
      fullWidth
      title={
        selectedTicket ? (
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1' }}>
              Manage Ticket {selectedTicket.ticketNumber}
            </Typography>
            <Typography variant="subtitle2" color="text.primary" sx={{ fontSize: '1rem' }}>
              {selectedTicket.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Chip
                label={selectedTicket.status}
                size="small"
                sx={{ ...getStatusStyle(theme, selectedTicket.status), fontSize: '1rem' }}
              />
              <Chip
                label={selectedTicket.priority}
                size="small"
                sx={{ ...getPriorityStyle(theme, selectedTicket.priority), fontSize: '1rem' }}
              />
              <Chip label={selectedTicket.category} size="small" variant="outlined" />
            </Box>
          </Box>
        ) : (
          'Manage Ticket'
        )
      }
      actions={<Button onClick={() => setOpenDialog(false)}>Close</Button>}
    >
      {selectedTicket && (
        <Box>
          <Box sx={{ mb: 3, p: 2, borderRadius: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1.1rem' }}>
              Ticket Information
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>User:</strong>{' '}
                  {selectedTicket.userName && selectedTicket.userName !== 'N/A'
                    ? selectedTicket.userName
                    : selectedTicket.userEmail}{' '}
                  ({selectedTicket.userEmail})
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>Team:</strong> {selectedTicket.teamName || 'Unknown Team'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>Client:</strong> {selectedTicket.clientName || selectedTicket.clientId}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>Created:</strong> {formatTimestamp(selectedTicket.createdAt)}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {canUpdateTickets && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1rem' }}>
                  Update Status
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <Select value={statusForm} onChange={handleStatusFormChange}>
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="waiting-response">Waiting Response</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Reason (optional)"
                  value={statusReason}
                  onChange={handleStatusReasonChange}
                  multiline
                  minRows={1}
                  maxRows={5}
                  sx={{ mb: 1 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleUpdateStatus}
                  startIcon={<ResolveIcon />}
                  sx={{
                    backgroundColor: theme.palette.bidlogiq.brick,
                    color: theme.palette.common.white,
                    fontSize: '1.1rem',
                    '&:hover': { backgroundColor: theme.palette.bidlogiq.brick },
                  }}
                >
                  Update Status
                </Button>
              </Grid>
            )}

            {canUpdateTickets && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1rem' }}>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      handleQuickResolve();
                      // Delay handleUpdateStatus slightly so state can catch up, or call directly with params in hook
                      setTimeout(handleUpdateStatus, 0);
                    }}
                    startIcon={<ResolveIcon />}
                    sx={{
                      backgroundColor: theme.palette.bidlogiq.brick,
                      color: theme.palette.common.white,
                      fontSize: '1.1rem',
                      '&:hover': { backgroundColor: theme.palette.bidlogiq.brick },
                    }}
                  >
                    Mark Resolved
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      handleQuickClose();
                      setTimeout(handleUpdateStatus, 0);
                    }}
                    startIcon={<CloseIcon />}
                    sx={{
                      backgroundColor: theme.palette.bidlogiq.brick,
                      color: theme.palette.common.white,
                      fontSize: '1.1rem',
                      '&:hover': { backgroundColor: theme.palette.bidlogiq.brick },
                    }}
                  >
                    Close Ticket
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1rem' }}>
            Conversation History
          </Typography>
          {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedTicket.responses.map((response: TicketResponse, index) => {
                const getBorderColor = () => {
                  if (response.isSystemMessage) {
                    return theme.palette.grey[400];
                  }
                  if (response.authorType === 'client_admin' || response.authorType === 'support') {
                    // Support/Admin border color
                    return theme.palette.bidlogiq.brick;
                  }
                  // User border color
                  return theme.palette.primary.main;
                };

                return (
                  <Card
                    key={response.id || index}
                    variant="outlined"
                    sx={{
                      borderLeft: `4px solid ${getBorderColor()}`,
                      ml: response.authorType === 'user' ? 0 : 4,
                      mr: response.authorType === 'user' ? 4 : 0,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <CardContent sx={{ pb: '16px !important' }}>
                      <Box
                        sx={{
                          width: '100%',
                          mb: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontSize: '1rem', fontWeight: 'bold' }}
                          >
                            {response.authorName ||
                              (response.authorType === 'client_admin' ? 'Support Team' : 'User')}
                          </Typography>
                          <Chip
                            label={
                              response.authorType === 'client_admin' ||
                              response.authorType === 'support'
                                ? 'Support'
                                : 'User'
                            }
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                          {response.isSystemMessage && (
                            <Chip
                              label="System"
                              size="small"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.85rem' }}
                        >
                          {formatTimestamp(response.timestamp as Date)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          width: '100%',
                          whiteSpace: 'pre-wrap',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {response.message || response.response}
                      </Typography>
                      {response.attachments && response.attachments.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {response.attachments.map((attachment) => (
                            <Link
                              key={attachment.url}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'block',
                                width: 80,
                                height: 80,
                                borderRadius: 1,
                                overflow: 'hidden',
                                boxShadow: 1,
                                '&:hover': { opacity: 0.8 },
                              }}
                            >
                              <img
                                src={attachment.url}
                                alt={attachment.filename}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </Link>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Typography>No responses yet.</Typography>
          )}

          {canUpdateTickets && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '1rem' }}>
                Add Response
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Type your response here..."
                value={responseForm}
                onChange={handleResponseFormChange}
                sx={{ mb: 2 }}
                slotProps={{
                  htmlInput: { maxLength: 5000 },
                }}
                helperText={`${responseForm.length}/5000`}
              />
              <Button
                variant="contained"
                onClick={handleAddResponse}
                disabled={!responseForm.trim()}
                startIcon={<ReplyIcon />}
                sx={{
                  backgroundColor: theme.palette.bidlogiq.brick,
                  color: theme.palette.common.white,
                  fontSize: '1.1rem',
                  '&:hover': { backgroundColor: theme.palette.bidlogiq.brick },
                }}
              >
                Send Response
              </Button>
            </Box>
          )}
        </Box>
      )}
    </BaseModal>
  );
};
